import { useCallback, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import { Modal } from './Modal'
import { Button } from './Button'
import { ConfirmContext, AlertContext, type ConfirmOptions, type AlertOptions } from './confirmDialogContext'

type ConfirmState = (ConfirmOptions & { resolve: (value: boolean) => void }) | null
type AlertState = (AlertOptions & { resolve: () => void }) | null

/** Sostituisce window.confirm()/alert(): entrambi bloccano il thread con un popup di sistema fuori
 * dal design; questi hook restituiscono una Promise risolta al click, mantenendo lo stesso `if (await confirm(...))`
 * che si scriveva con `if (window.confirm(...))`, ma con un modal coerente col resto dell'app. */
export function ConfirmDialogProvider({ children }: { children: ReactNode }) {
  const [confirmState, setConfirmState] = useState<ConfirmState>(null)
  const [alertState, setAlertState] = useState<AlertState>(null)
  const confirmRef = useRef(confirmState)
  confirmRef.current = confirmState

  const confirm = useCallback((options: ConfirmOptions | string) => {
    const opts = typeof options === 'string' ? { message: options } : options
    return new Promise<boolean>((resolve) => setConfirmState({ ...opts, resolve }))
  }, [])

  const alertUser = useCallback((options: AlertOptions | string) => {
    const opts = typeof options === 'string' ? { message: options } : options
    return new Promise<void>((resolve) => setAlertState({ ...opts, resolve }))
  }, [])

  function closeConfirm(result: boolean) {
    confirmState?.resolve(result)
    setConfirmState(null)
  }

  function closeAlert() {
    alertState?.resolve()
    setAlertState(null)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      <AlertContext.Provider value={alertUser}>
        {children}

        <Modal show={!!confirmState} onClose={() => closeConfirm(false)} title={confirmState?.title ?? 'Conferma'} size={confirmState?.size ?? 'sm'}>
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="mt-0.5 shrink-0 text-notice" />
            <p className="m-0 text-sm text-body">{confirmState?.message}</p>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline-secondary" size="sm" onClick={() => closeConfirm(false)}>
              {confirmState?.cancelLabel ?? 'Annulla'}
            </Button>
            <Button variant={confirmState?.variant ?? 'danger'} size="sm" onClick={() => closeConfirm(true)}>
              {confirmState?.confirmLabel ?? 'Conferma'}
            </Button>
          </div>
        </Modal>

        <Modal show={!!alertState} onClose={closeAlert} title={alertState?.title ?? 'Attenzione'} size="sm">
          <div className="flex items-start gap-3">
            <Info size={20} className="mt-0.5 shrink-0 text-informative" />
            <p className="m-0 text-sm text-body">{alertState?.message}</p>
          </div>
          <div className="mt-4 flex justify-end">
            <Button variant="primary" size="sm" onClick={closeAlert}>
              {alertState?.okLabel ?? 'OK'}
            </Button>
          </div>
        </Modal>
      </AlertContext.Provider>
    </ConfirmContext.Provider>
  )
}
